/**
 * Series Notes (#676 / #696 / #1025). Writer-owned people / world / custom
 * snapshots at series altitude. The long write-up lives on the same
 * note. Offer or invoke — never silent-fill. Generate-from-chats writes
 * through this same store after the writer accepts.
 */

import { MAKE_A_NOTE, SERIES_NOTE_EMPTY } from './writerCopy';

export const SNAPSHOT_KINDS = ['people', 'world', 'custom'];

export const SNAPSHOT_EMPTY = SERIES_NOTE_EMPTY;

export const MAKE_A_NOTE_LABEL = MAKE_A_NOTE;
export const MAKE_A_CARD = MAKE_A_NOTE;

export const OPEN_WRITEUP = 'Open long write-up';

export const EMPTY_SNAPSHOTS = { version: 1, cards: [] };

export function seriesCardsPath(projectId, search = '') {
  const base = projectId ? `/projects/${projectId}/series-cards` : '/projects';
  if (!search) return base;
  const qs = String(search).replace(/^\?/, '');
  return qs ? `${base}?${qs}` : base;
}

/** Leftover Series Dossiers URL. Not a working room. */
export function seriesDossiersPath(projectId) {
  return projectId ? `/projects/${projectId}/series-dossiers` : '/projects';
}

export function isSeriesCardsPath(pathname) {
  return /^\/projects\/[^/]+\/series-cards\/?$/.test(pathname || '');
}

export function isSeriesDossiersPath(pathname) {
  return /^\/projects\/[^/]+\/series-dossiers\/?$/.test(pathname || '');
}

export function isSeriesSnapshotPath(pathname) {
  return isSeriesCardsPath(pathname) || isSeriesDossiersPath(pathname);
}

export function emptySnapshots() {
  return { version: 1, cards: [] };
}

export function cardsOfKind(store, kind) {
  return ((store && store.cards) || []).filter((card) => card.kind === kind);
}

export function worldCard(store) {
  return cardsOfKind(store, 'world')[0] || null;
}

export function cardWriteup(card) {
  return ((card && card.writeup) || '').trim();
}

export function hasCardWriteup(card) {
  return Boolean(cardWriteup(card));
}

export function newCardId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `card-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function asSearchParams(search) {
  if (search instanceof URLSearchParams) return search;
  const raw = search == null ? '' : String(search);
  return new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
}

/**
 * Leftover /series-dossiers → Series Notes (the named card, or the pile).
 * writeup=1 asks the Cards room to open the long write-up when one exists.
 * A thin card still lands on the short version, not an empty second page.
 */
export function leftoverSeriesDossiersToCardsPath(projectId, search = '') {
  const params = asSearchParams(search);
  const next = new URLSearchParams();
  const kind = params.get('kind');
  const name = params.get('name');
  if (kind === 'world') next.set('kind', 'world');
  else if (kind === 'people' || name) next.set('kind', 'people');
  if (name) next.set('name', name);
  if (kind || name) next.set('writeup', '1');
  return seriesCardsPath(projectId, next.toString());
}

export function findSeriesCard(store, { kind, name } = {}) {
  const cards = (store && store.cards) || [];
  if (kind === 'world') return worldCard(store);
  const wanted = (name || '').trim().toLowerCase();
  if (!wanted) return null;
  const pool = kind ? cardsOfKind(store, kind) : cards;
  return pool.find((card) => (card.name || '').trim().toLowerCase() === wanted) || null;
}

export function upsertCard(store, card) {
  const nextCard = {
    ...card,
    writeup: card.writeup == null ? '' : card.writeup,
  };
  const cards = [...((store && store.cards) || [])];
  const index = cards.findIndex((row) => row.id === nextCard.id);
  if (nextCard.kind === 'world') {
    const withoutWorld = cards.filter((row) => row.kind !== 'world' || row.id === nextCard.id);
    const worldIndex = withoutWorld.findIndex((row) => row.id === nextCard.id);
    if (worldIndex >= 0) withoutWorld[worldIndex] = nextCard;
    else withoutWorld.push(nextCard);
    return { version: 1, cards: withoutWorld };
  }
  if (index >= 0) cards[index] = nextCard;
  else cards.push(nextCard);
  return { version: 1, cards };
}

export function removeCard(store, cardId) {
  return {
    version: 1,
    cards: ((store && store.cards) || []).filter((card) => card.id !== cardId),
  };
}
